import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic would go here
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <div className="min-h-screen bg-white pt-20" data-id="element-330">
      {/* Hero Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white" data-id="element-331">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center" data-id="element-332">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-4 sm:mb-6" data-id="element-333">
            Get In Touch
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed" data-id="element-334">
            Ready to start your digital transformation journey? Let's discuss
            how we can help achieve your business goals
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 sm:py-20" data-id="element-335">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-336">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16" data-id="element-337">
            {/* Contact Information */}
            <div data-id="element-338">
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-6 sm:mb-8" data-id="element-339">
                Contact Information
              </h2>

              <div className="space-y-6 mb-8 sm:mb-12" data-id="element-340">
                <ContactInfoItem icon={Mail} title="Email" content="info@manueltechnologies.com" link="mailto:info@manueltechnologies.com" data-id="element-341" />
                <ContactInfoItem icon={Phone} title="Phone" content="08160027317" link="tel:08160027317" data-id="element-342" />
                <ContactInfoItem icon={MapPin} title="Address" content="Lagos, Nigeria" data-id="element-343" />
              </div>

              <div className="p-6 sm:p-8 bg-slate-50 rounded-2xl" data-id="element-344">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4" data-id="element-345">
                  Business Hours
                </h3>
                <div className="space-y-3 text-sm sm:text-base text-slate-700" data-id="element-346">
                  <div className="flex justify-between gap-4" data-id="element-347">
                    <span className="font-bold" data-id="element-348">Monday - Friday:</span>
                    <span data-id="element-349">8:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-350">
                    <span className="font-bold" data-id="element-351">Saturday:</span>
                    <span data-id="element-352">9:00 AM - 2:00 PM</span>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-353">
                    <span className="font-bold" data-id="element-354">Sunday:</span>
                    <span data-id="element-355">Closed</span>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-6 sm:mt-8 aspect-video bg-slate-100 rounded-2xl flex items-center justify-center" data-id="element-356">
                <div className="text-center p-4" data-id="element-357">
                  <MapPin className="w-12 h-12 sm:w-16 sm:h-16 text-slate-400 mx-auto mb-4" data-id="element-358" />
                  <div className="text-slate-600 font-bold text-sm sm:text-base" data-id="element-359">
                    Map Location
                  </div>
                  <div className="text-xs sm:text-sm text-slate-500" data-id="element-360">
                    Lagos, Nigeria
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div data-id="element-361">
              <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-slate-200" data-id="element-362">
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-6" data-id="element-363">
                  Send Us a Message
                </h2>

                {isSubmitted ? <div className="py-12 text-center" data-id="element-364">
                    <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full mb-6" data-id="element-365">
                      <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" data-id="element-366" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2" data-id="element-367">
                      Message Sent!
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600" data-id="element-368">
                      Thank you for reaching out. We'll get back to you within
                      24 hours.
                    </p>
                  </div> : <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-id="element-369">
                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-370">
                      <div data-id="element-371">
                        <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-372">
                          Full Name *
                        </label>
                        <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm sm:text-base" placeholder="John Doe" data-id="element-373" />
                      </div>
                      <div data-id="element-374">
                        <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-375">
                          Email Address *
                        </label>
                        <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm sm:text-base" placeholder="john@example.com" data-id="element-376" />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-377">
                      <div data-id="element-378">
                        <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-379">
                          Phone Number
                        </label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm sm:text-base" placeholder="08160027317" data-id="element-380" />
                      </div>
                      <div data-id="element-381">
                        <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-382">
                          Company Name
                        </label>
                        <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm sm:text-base" placeholder="Your Company" data-id="element-383" />
                      </div>
                    </div>

                    <div data-id="element-384">
                      <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-385">
                        Service Interested In *
                      </label>
                      <select name="service" required value={formData.service} onChange={handleChange} className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-sm sm:text-base" data-id="element-386">
                        <option value="" data-id="element-387">Select a service</option>
                        <option value="ai" data-id="element-388">AI & Machine Learning</option>
                        <option value="software" data-id="element-389">Software Development</option>
                        <option value="cloud" data-id="element-390">Cloud Solutions</option>
                        <option value="security" data-id="element-391">Cybersecurity</option>
                        <option value="analytics" data-id="element-392">Data Analytics</option>
                        <option value="consulting" data-id="element-393">IT Consulting</option>
                        <option value="writing" data-id="element-394">Business Writing</option>
                        <option value="other" data-id="element-395">Other</option>
                      </select>
                    </div>

                    <div data-id="element-396">
                      <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-397">
                        Message *
                      </label>
                      <textarea name="message" required value={formData.message} onChange={handleChange} rows={6} className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors resize-none text-sm sm:text-base" placeholder="Tell us about your project..." data-id="element-398" />
                    </div>

                    <button type="submit" className="w-full px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-base sm:text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2" data-id="element-399">
                      Send Message
                      <Send className="w-5 h-5" data-id="element-400" />
                    </button>
                  </form>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-401">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-402">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4 sm:mb-6" data-id="element-403">
            Prefer to Schedule a Call?
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 mb-8 sm:mb-10" data-id="element-404">
            Book a free 30-minute consultation with our team
          </p>
          <a href="tel:08160027317" className="inline-block px-6 sm:px-8 py-3 sm:py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold text-base sm:text-lg transition-all duration-300 hover:scale-105" data-id="element-405">
            Call 08160027317
          </a>
        </div>
      </section>
    </div>;
}
function ContactInfoItem({
  icon: Icon,
  title,
  content,
  link
}: {
  icon: any;
  title: string;
  content: string;
  link?: string;
}) {
  const Wrapper = link ? 'a' : 'div';
  const wrapperProps = link ? {
    href: link,
    className: 'hover:text-blue-600 transition-colors'
  } : {};
  return <div className="flex items-start gap-4" data-id="element-406">
      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-407">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" data-id="element-408" />
      </div>
      <div data-id="element-409">
        <div className="font-bold text-slate-900 mb-1 text-sm sm:text-base" data-id="element-410">
          {title}
        </div>
        <Wrapper {...wrapperProps} className={`text-slate-700 text-sm sm:text-base break-all ${link ? 'hover:text-blue-600 transition-colors' : ''}`} data-id="element-411">
          {content}
        </Wrapper>
      </div>
    </div>;
}